// REACT IMPORTS
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

// COMPONENT IMPORTS
import InputField from "components/form/InputField";
import Button from "components/form/Button";
import { useAppDispatch } from "store/hooks";
import {
  signUpDetailsGet,
  signUpUser,
  useAccountDetails,
} from "store/slices/authSlice";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import { decode } from "utils/HelperFunctions";
import { SettingsValue } from "types/payments";
import { useTid } from "utils/testid";
import { activateOnKey } from "utils/activateOnKey";

/* ------------------------------------- SIGN UP WITH EMAIL ATTACHED FORM ------------------------------------- */

const SignUpEmailAttachedForm = () => {
  const tid = useTid();
  const { state } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const accountDetails = useAccountDetails();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const initialVal = {
    email: state?.email,
    accounts_id: "",
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [values, setValues] = useState<any>(initialVal);
  const [isErr, setIsErr] = useState<boolean>(false);

  useEffect(() => {
    if (isErr) {
      setIsErr(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const onSubmit = async (e: any) => {
    e.preventDefault();
    onSentOtp();
  };

  useEffect(() => {
    setValues({ ...values, email: state?.email || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const normalizeAccountId = (accountId: unknown) =>
    String(accountId || "")
      .trim()
      .replace(/[\s-]/g, "")
      .toLowerCase();

  const getSelectedAccount = (accounts: any[], accountId: string) => {
    if (!Array.isArray(accounts)) {
      return undefined;
    }

    const enteredAccountId = normalizeAccountId(accountId);

    return accounts.find(
      (item: any) => normalizeAccountId(item?.accountid) === enteredAccountId
    );
  };

  const onSentOtp = async () => {
    setIsLoading(true);
    try {
      const normalizedValues = {
        email: String(values?.email || "").trim(),
        accounts_id: String(values?.accounts_id || "").trim(),
      };
      const accountResponse: any = await dispatch(
        signUpDetailsGet({
          email: normalizedValues.email,
          accountid: normalizedValues.accounts_id,
          clusterId: clusterId,
        })
      ).unwrap();

      if (!accountResponse?.body?.success) {
        setIsErr(true);
        return;
      }

      const account = getSelectedAccount(
        accountResponse?.body?.data?.getAccounts,
        normalizedValues.accounts_id
      );

      if (!account) {
        setIsErr(true);
        return;
      }

      const canonicalAccountId = String(account?.accountid || "").trim();

      if (!canonicalAccountId) {
        setIsErr(true);
        return;
      }

      if (String(account?.isPasswordSet).toLowerCase() === "true") {
        navigate(`/${cluster_id}/sign-up-password`, {
          state: { email: normalizedValues.email },
        });
        return;
      }

      const payload: any = {
        email: normalizedValues.email,
        accounts_id: canonicalAccountId,
        isEmailMutiple: "multiple",
        clusterId: clusterId,
      };
      const res: any = await dispatch(signUpUser(payload)).unwrap();
      if (res?.body?.success) {
        const passwordExists = res?.body?.data?.passwordExists;
        const isPasswordSet =
          typeof passwordExists === "boolean"
            ? passwordExists
            : String(passwordExists ?? account?.isPasswordSet).toLowerCase() ===
              "true";

        if (isPasswordSet) {
          navigate(`/${cluster_id}/sign-up-password`, {
            state: { email: normalizedValues.email },
          });
        } else {
          const internalAccountId =
            res?.body?.data?.accounts_id ?? account?.accounts_id;

          if (internalAccountId === undefined || internalAccountId === null) {
            setIsErr(true);
            return;
          }

          navigate(`/${cluster_id}/sign-up-otp-verification`, {
            state: {
              data: {
                getAccounts: [{ ...account, accounts_id: internalAccountId }],
                isEmailMutiple: "multiple",
              },
              email: normalizedValues.email,
              accounts_id: payload.accounts_id,
            },
          });
        }
      } else {
        setIsErr(true);
      }
    } catch (error: any) {
      setIsErr(true);
      console.log("send verification code error: -->", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const checkIsDisabled = () => {
    return Object.values(values).some(
      (value) => String(value || "").trim() === ""
    );
  };

  return (
    <div
      className="animate__animated animate__fadeIn"
      data-testid={tid("form-shell")}
    >
      <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
        Welcome - Secure Payment Portal
      </div>
      <p className="lg:py-10 pt-6 pb-10 max-w-[520px] text-txtParagraph lg:text-base text-sm leading-normal font-normal">
        Please enter your email and account ID to access your account.
      </p>
      <form onSubmit={onSubmit} data-testid={tid("attached-account-form")}>
        <div className="max-w-[520px] flex flex-col gap-10 lg:gap-[60px]">
          <div className="flex flex-col lg:gap-6 gap-5">
            <InputField
              type="email"
              name="email"
              label="Email"
              data-testid={tid("email-input")}
              aria-label="Email address"
              placeholder="Enter your email address"
              value={values?.email}
              onChange={(e: any) => {
                setValues({
                  ...values,
                  email: e?.target?.value,
                });
              }}
            />
            <InputField
              name="accounts_id"
              label="Account id"
              data-testid={tid("account-id-input")}
              aria-label="Account ID"
              placeholder="Enter Account ID"
              value={values?.accounts_id}
              onChange={(e: any) => {
                setValues({
                  ...values,
                  accounts_id: e?.target?.value,
                });
              }}
            />
          </div>
          <div>
            <Button
              className="w-full"
              text="Next"
              type="submit"
              data-testid={tid("next-button")}
              aria-label="Continue sign up with account ID"
              isDisabled={checkIsDisabled()}
              isLoading={isLoading}
              variant="variable"
            />
            {!isErr && (
              <>
                <div className="pt-[12px] lg:text-base text-sm w-full grid place-items-center">
                  <span
                    className="text-blue hover:!text-btnPrimaryHover lg:py-[10px] lg:px-4 cursor-pointer"
                    onClick={() => navigate(-1)}
                    onKeyDown={activateOnKey(() => navigate(-1))}
                    role="button"
                    tabIndex={0}
                    data-testid={tid("go-back-button")}
                    aria-label="Go back"
                  >
                    Go back
                  </span>
                </div>
                <div className="w-full h-[1px] bg-strokePrimary lg:mt-4 mt-3"></div>
              </>
            )}
            {isErr && (
              <>
                <div
                  className="flex items-center gap-[5px] mt-3 px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
                  role="alert"
                  data-testid={tid("account-id-not-recognized-alert")}
                >
                  <InfoRedCircleIcon aria-hidden="true" />
                  <div className="lg:text-base text-sm text-red leading-[20.83px]">
                    Account ID not recognized.
                  </div>
                </div>
                {accountDetails?.data &&
                  accountDetails?.data.request_access === SettingsValue.YES && (
                    <div className="mt-4 text-iconBright lg:text-base text-sm leading-[20.83px] animate__animated animate__fadeIn">
                      <p>
                        It looks like this account ID isn't associated with your
                        email. If you're new here,{" "}
                        <Link
                          to={`/${cluster_id}/request-access`}
                          className="text-blue hover:!text-btnPrimaryHover"
                          data-testid={tid("request-access-link")}
                          aria-label="Request access"
                        >
                          request access
                        </Link>{" "}
                        to join us!
                      </p>
                    </div>
                  )}
                <div className="pt-4 lg:text-base text-sm text-blue hover:!text-btnPrimaryHover leading-normal font-medium flex justify-center">
                  <div
                    className="cursor-pointer"
                    onClick={() => navigate(-1)}
                    onKeyDown={activateOnKey(() => navigate(-1))}
                    role="button"
                    tabIndex={0}
                    data-testid={tid("go-back-button")}
                    aria-label="Go back"
                  >
                    Go back
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </form>
      {!isErr && (
        <div className="max-w-[520px] flex flex-col gap-4 items-center lg:pt-4 pt-3">
          <div className="flex gap-[6px] items-center justify-start w-full">
            <p className="lg:text-base text-sm text-txtParagraph leading-normal">
              Already have an account?
            </p>
            <Link
              to={`/${cluster_id}/login`}
              className="cursor-pointer lg:text-base text-sm text-blue hover:!text-btnPrimaryHover font-medium leading-normal"
              data-testid={tid("login-link")}
              aria-label="Log in instead"
            >
              Login
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUpEmailAttachedForm;
